import { defineStore } from 'pinia';
import type { LoadingStates } from '@/models/loadingStates';
import { apiServices, SchoolAPIService } from '@/services/api';
import { dateService } from '@/services/date';
import { createDefaultMetrics } from '@/utils/metricsFactory';
import { LOCATIONS } from '@/config/locations';

export const useMetricsStore = defineStore('metrics', {
  state: () => ({
    metrics: createDefaultMetrics(),
    loadingStates: {} as LoadingStates,
    totalDifferenceLoadingStates: {} as LoadingStates,
    errorStates: {} as Record<string, boolean>,
    loadTime: 0,
    selectedDate: dateService.toLocalDateString(new Date())
  }),

  persist: {
    omit: ['selectedDate', 'errorStates'],
    afterHydrate: ({ store }) => store.removeUnknownLocations()
  },

  getters: {
    failedLocations(state): string[] {
      return Object.keys(state.errorStates).filter((location) => state.errorStates[location]);
    }
  },

  actions: {
    // Persisted state can still contain locations that have since been removed from LOCATIONS.
    // They would otherwise be rendered forever with frozen values, because they are never fetched again.
    removeUnknownLocations(): void {
      const knownLocations = new Set(LOCATIONS.map((location) => location.name));

      [this.metrics, this.loadingStates, this.totalDifferenceLoadingStates, this.errorStates].forEach(
        (locationState: Record<string, unknown>) => {
          Object.keys(locationState)
            .filter((location) => !knownLocations.has(location))
            .forEach((location) => delete locationState[location]);
        }
      );
    },

    initializeLoadingStates(): void {
      LOCATIONS.forEach((location) => {
        this.loadingStates[location.name] = true;
        this.totalDifferenceLoadingStates[location.name] = true;
        this.errorStates[location.name] = false;
      });
    },

    async fetchLocationMetrics(location: string, service: SchoolAPIService): Promise<void> {
      try {
        const [dashboardData, financeData, staffData] = await Promise.all([
          service.getDashboardMetrics(this.selectedDate),
          service.getFinanceMetrics(this.selectedDate),
          service.getStaffMetrics(this.selectedDate)
        ]);

        this.metrics[location] = {
          activeStudents: dashboardData?.active_students ?? 0,
          activeEmployees: dashboardData?.active_employees ?? 0,
          feeSum: financeData?.fee_sum ?? 0,
          feeSumActive: financeData?.fee_sum_active ?? 0,
          feeSumDifference: financeData?.fee_sum_difference ?? 0,
          studentTarget: dashboardData?.student_target ?? null,

          payingStudents: financeData?.paying_students ?? 0,
          averageSchoolFee: financeData?.average_school_fee ?? 0,
          lastBankImportAt: dateService.parseApiDate(financeData?.last_bank_import?.imported_at),
          lastBankImportPeriodEnd: dateService.parseApiDate(financeData?.last_bank_import?.period_end),
          bankBalance: financeData?.bank_balance ?? 0,
          lastPaymentReminderDate: dateService.parseApiDate(financeData?.last_payment_reminder_date),
          openPaymentReminderSum: financeData?.open_payment_reminder_sum ?? 0,
          paymentRemindersSentLastMonth: financeData?.payment_reminders_sent_last_month ?? 0,
          financialCertsCreated: financeData?.financial_certs_created ?? 0,
          financialCertsSent: financeData?.financial_certs_sent ?? 0,
          schoolFeeReductionCount: financeData?.school_fee_reductions?.count ?? 0,
          schoolFeeReductionTotal: financeData?.school_fee_reductions?.total ?? 0,
          schoolFeeReductionsByType:
            financeData?.school_fee_reductions?.by_type?.map((item) => ({
              reasonId: item.reason_id,
              reason: item.reason,
              count: item.count,
              total: item.total
            })) ?? [],

          totalDifference: this.metrics[location]?.totalDifference ?? null,
          totalDifferenceBySchoolYear: this.metrics[location]?.totalDifferenceBySchoolYear ?? [],

          averageAge: staffData?.average_age ?? 0,
          amountMale: staffData?.amount_male ?? 0,
          amountFemale: staffData?.amount_female ?? 0,
          teacherCount: staffData?.teacher_count ?? 0,
          staffCount: staffData?.staff_count ?? 0,
          pedagogicalHours: staffData?.hours?.pedagogical?.hours ?? 0,
          pedagogicalLessons: staffData?.hours?.pedagogical?.lessons ?? 0,
          nonPedagogicalHours: staffData?.hours?.non_pedagogical?.hours ?? 0,
          hoursByCategory: staffData?.hours?.by_category ?? [],
          unassignedHours: {
            pedagogical: staffData?.hours?.unassigned?.pedagogical ?? { hours: 0, lessons: 0 },
            non_pedagogical: staffData?.hours?.unassigned?.non_pedagogical ?? { hours: 0, lessons: 0 }
          }
        };

        this.errorStates[location] = dashboardData === null && financeData === null && staffData === null;
      } catch (error) {
        console.error(`Error fetching metrics for ${location}:`, error);
        this.errorStates[location] = true;
      } finally {
        this.loadingStates[location] = false;
      }
    },

    async fetchLocationTotalDifference(location: string, service: SchoolAPIService, forceRefresh = false): Promise<void> {
      try {
        const data = await service.getTotalDifference(this.selectedDate, forceRefresh);
        this.metrics[location].totalDifference = data.total_difference;
        this.metrics[location].totalDifferenceBySchoolYear =
          data.school_years?.map((schoolYear) => ({
            id: schoolYear.id,
            name: schoolYear.name,
            start: dateService.parseApiDate(schoolYear.start),
            isCurrent: schoolYear.is_current,
            totalDifference: schoolYear.total_difference
          })) ?? [];
      } catch (error) {
        console.warn(`Error fetching total difference for ${location}:`, error);
      } finally {
        this.totalDifferenceLoadingStates[location] = false;
      }
    },

    async fetchAllMetrics(forceRefreshTotalDifference = false): Promise<void> {
      const startTime = performance.now();

      const fetchPromises = Object.entries(apiServices).map(([location, service]) => this.fetchLocationMetrics(location, service));

      await Promise.all(fetchPromises);

      const endTime = performance.now();
      this.loadTime = parseFloat((endTime - startTime).toFixed(2));

      // Total difference is not awaited because it takes multiple seconds and should not block the rest of the dashboard from rendering.
      Object.entries(apiServices).forEach(([location, service]) => {
        this.fetchLocationTotalDifference(location, service, forceRefreshTotalDifference);
      });
    },

    async updateSelectedDate(date: string) {
      this.selectedDate = date;
      this.initializeLoadingStates();
      await this.fetchAllMetrics();
    },

    async refreshAllMetrics() {
      this.initializeLoadingStates();
      await this.fetchAllMetrics(true);
    }
  }
});
